/**
 * WebSmKeyHandoff — the key becomes the invite. Plays the key-turn video and,
 * once it settles face-on, fades our real (scannable) QR over the placeholder
 * baked into the plate so the code is ours. The page is just the key — no
 * copy, no buttons.
 *
 * The clip is opaque h264 on pure black, so the backdrop is pure black too
 * and the key simply merges (no alpha video, no blending).
 *
 * Options:
 *   QrData                      text/URL to encode as a REAL scannable QR
 *                                 (overlaid on the video's QR; none until
 *                                 it is set — the plate keeps its own)
 *   QrImage                     alternative: URL of a pre-made QR image
 *   QrLeft / QrTop / QrSize     overlay placement over the video QR
 *   QrRadius                    overlay corner rounding
 *   AutoAdvanceMs               if > 0, advance this many ms after the video
 *   Name                        engraved along the shaft (default: the name
 *                                 they engraved on their key, on this device)
 *   NameIsOwner                 "1": the key is someone else's — only Name
 *                                 is engraved, never this device's name
 *   BgColor
 *
 * Emits CLICK1 only if AutoAdvanceMs is set.
 */
import { useEffect, useRef, useState, type CSSProperties } from "react";
import { TexturedQr } from "./textured-qr";
import { lastName, useMemberName } from "./operative-name";
import { ORIGINAL_MATRIX } from "./original-matrix";
import type { StepTypeProps } from "./types";
import keyVideo from "./key-qr-handoff.mp4";
import keyPoster from "./key-qr-handoff-poster.webp";

/** How long the key's front, with their name, is held before it turns. */
const NAME_HOLD_MS = 1600;
// the code's re-engraving (smQrCut), then the zoom onto it
const QR_CUT_MS = 1300;
// The video runs ~1.7s; fall back to reveal if `onEnded` never fires.
/** The shaft on the clip's FIRST frame (720x1280) — the key's front, before
 *  it turns to show the QR side — as fractions: where their name is engraved. */
const SHAFT = { top: 0.46, bottom: 0.69, centerX: 0.494 };
const VIDEO_MS = 1800;

export default function WebSmKeyHandoffType({
  getOpt,
  dispatch,
}: StepTypeProps) {
  const bg = getOpt("BgColor") || "#000000";

  // No stand-in while the real link loads: the bare site would be a code
  // anyone could scan, and it would be cut and zoomed on as if it were theirs.
  const qrData = getOpt("QrData");
  // ?original=1 → render the decorative replica of the video's own QR instead
  // of a real scannable code.
  const original = getOpt("Original") === "1";
  const qrImage = getOpt("QrImage");
  // Measured off the clip's last frame (720×1280): the plate's inner square.
  const qrLeft = getOpt("QrLeft") || "49.4%";
  const qrTop = getOpt("QrTop") || "30.1%";
  const qrSize = getOpt("QrSize") || "17.8%";
  const qrRadius = getOpt("QrRadius") || "3%";
  // The clip's plate has a placeholder code baked in, so the overlay is an
  // opaque brass plate in the same tone, and only the modules are drawn on top.
  const plate =
    getOpt("QrPlate") || "linear-gradient(160deg,#b08a6c,#8f6a4c 55%,#6f4d33)";
  const autoMs = parseInt(getOpt("AutoAdvanceMs"), 10) || 0;
  // the name they engraved on the key they earned
  // (the texted link passes the key owner's name, for a device that never
  // saw it engraved)
  const { data: memberName = "" } = useMemberName();
  // (or the one typed on this device, if saving it to their account failed)
  // Someone else's key (the texted link) shows its owner's name or none —
  // never the viewer's own while the owner's loads or fails.
  const ownerOnly = getOpt("NameIsOwner") === "1";
  const name = (getOpt("Name") || (ownerOnly ? "" : memberName || lastName()))
    .trim()
    .toUpperCase();
  const hasOverlay = Boolean(qrData || qrImage);

  const videoRef = useRef<HTMLVideoElement>(null);
  const [settled, setSettled] = useState(false);
  // the clip has started turning the key
  const [playing, setPlaying] = useState(false);

  // Force muted (React doesn't reliably set the muted *property*) and kick
  // play() so autoplay isn't blocked and the video doesn't sit black. With a
  // name on the key, it is held on its front first so the name can be read.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = true;
    const t = setTimeout(
      () => v.play().catch(() => {}),
      name ? NAME_HOLD_MS : 0,
    );
    return () => clearTimeout(t);
    // the name is read once, on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reveal the QR when the video ends; a fallback covers a blocked `onEnded`.
  // The fallback is armed from `playing`, not from mount: autoplay can start
  // late, and a mount-based timer would re-engrave the plate while the key was
  // still mid-turn.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    let t: ReturnType<typeof setTimeout>;
    const arm = () => {
      clearTimeout(t);
      const left = Number.isFinite(v.duration)
        ? Math.max(0, (v.duration - v.currentTime) * 1000)
        : VIDEO_MS;
      t = setTimeout(() => setSettled(true), left + 500);
    };
    const start = () => setPlaying(true);
    v.addEventListener("playing", arm);
    v.addEventListener("playing", start);
    if (!v.paused) {
      arm();
      start();
    }
    return () => {
      clearTimeout(t);
      v.removeEventListener("playing", arm);
      v.removeEventListener("playing", start);
    };
  }, []);

  // Once the new code is cut, zoom in on it so it is easy to scan: the QR
  // to the middle of the screen at about 72% of its width.
  const qrRef = useRef<HTMLDivElement>(null);
  const [zoom, setZoom] = useState(1);
  useEffect(() => {
    if (!settled) return;
    const t = setTimeout(() => {
      const qr = qrRef.current;
      // the screen it plays in (a phone-shaped column on a wide window)
      const frame = qr?.closest("section");
      if (qr?.offsetWidth && frame)
        setZoom(
          Math.max(
            1,
            Math.min(frame.clientWidth * 0.72, frame.clientHeight * 0.5) /
              qr.offsetWidth,
          ),
        );
    }, QR_CUT_MS);
    return () => clearTimeout(t);
    // the link can land after the key has settled: zoom once the code is cut
  }, [settled, hasOverlay]);

  // Optional auto-advance once the key has settled on the QR.
  useEffect(() => {
    if (!settled || autoMs <= 0) return;
    const t = setTimeout(() => {
      dispatch("CLICK1");
    }, autoMs);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settled, autoMs]);

  const sectionVars = { "--sm-bg": bg } as CSSProperties;

  return (
    <section
      style={sectionVars}
      className="relative flex h-[100dvh] items-center justify-center overflow-hidden bg-[var(--sm-bg)] p-0"
    >
      {/* the key — the whole page (portrait 720:1280). Width is derived from the
          page HEIGHT so the empty sides crop off and the key stays centred. */}
      {/* 1.24× the height-derived width: the key fills more of the screen and
          the empty sides crop off, which the section already hides. */}
      <div
        style={
          zoom > 1
            ? {
                transformOrigin: `${qrLeft} ${qrTop}`,
                transform: `translate(calc(50% - ${qrLeft}), calc(50% - ${qrTop})) scale(${zoom})`,
              }
            : undefined
        }
        className="relative aspect-[720/1280] w-[calc(100dvh*720/1280*1.24)] transition-transform duration-[900ms] ease-[cubic-bezier(.3,.7,.2,1)] [container-type:size]"
      >
        {/* their name, engraved along the shaft of the key's front as on the
            key they earned (key-engraving.tsx). It turns away with the key:
            narrowing and fading as the clip turns it to the QR side. */}
        {name && !settled ? (
          <div
            aria-hidden
            style={{
              left: `${SHAFT.centerX * 100}%`,
              top: `${((SHAFT.top + SHAFT.bottom) / 2) * 100}%`,
              width: `${(SHAFT.bottom - SHAFT.top) * 100}cqh`,
              fontSize: `${Math.min(3.2, ((SHAFT.bottom - SHAFT.top) * 100 * 0.9) / Math.max(1, name.length * 0.72))}cqh`,
            }}
            className={`pointer-events-none absolute z-[2] flex -translate-x-1/2 -translate-y-1/2 -rotate-90 ${playing ? "animate-[smNameTurn_.75s_ease-in_.25s_both]" : ""} items-center justify-center font-stencil leading-none tracking-[.14em] whitespace-nowrap text-[#2b1a0b] uppercase [text-shadow:0_1px_0_rgba(255,214,150,.45),0_-1px_0_rgba(0,0,0,.6)]`}
          >
            {name}
          </div>
        ) : null}
        {/* generated QR, laid over the key's head once it settles */}
        {hasOverlay ? (
          <div
            ref={qrRef}
            // Placement over the video's QR is authored per adventure, so it
            // stays inline.
            style={{
              left: qrLeft,
              top: qrTop,
              width: qrSize,
              transform: "translate(-50%,-50%)",
            }}
            className="pointer-events-none absolute z-[2] aspect-square"
          >
            {/* The new code is not dropped on top of the old one — a cutting
                head runs down the plate and the code is re-engraved in its
                wake. The plate is clipped to the part the head has passed,
                so above the line it is the new code and below it the plate's
                original one is still showing. */}
            <div
              style={{ borderRadius: qrRadius, background: plate }}
              className={`absolute inset-0 overflow-hidden ${
                settled
                  ? "animate-[smQrCut_1.1s_cubic-bezier(.45,0,.25,1)_both]"
                  : "opacity-0"
              }`}
            >
              {qrData ? (
                <TexturedQr
                  data={qrData}
                  matrix={original ? ORIGINAL_MATRIX : undefined}
                  transparent
                />
              ) : (
                <img
                  src={qrImage}
                  alt="Your invite QR code"
                  className="block h-full w-full object-contain"
                />
              )}
            </div>

            {/* the cutting head itself */}
            {settled ? (
              <div className="absolute inset-x-[-6%] h-[3px] animate-[smQrHead_1.1s_cubic-bezier(.45,0,.25,1)_both] bg-[linear-gradient(90deg,transparent,#ffe6a8,#fff6dd,#ffe6a8,transparent)] [box-shadow:0_0_14px_rgba(255,214,120,.95),0_0_34px_rgba(255,190,90,.6)]" />
            ) : null}

            {/* the heat it leaves behind, fading out */}
            {settled ? (
              <div
                style={{ borderRadius: qrRadius }}
                className="absolute inset-0 animate-[smQrGlow_1.6s_ease-out_both] bg-[radial-gradient(circle_at_50%_50%,rgba(255,200,110,.35),transparent_70%)] mix-blend-screen"
              />
            ) : null}
          </div>
        ) : null}

        <video
          ref={videoRef}
          // its first frame, so the key's front shows while it is held
          poster={keyPoster}
          muted
          playsInline
          preload="auto"
          onEnded={() => setSettled(true)}
          className="relative z-[1] block h-full w-full object-contain"
        >
          <source src={keyVideo} type="video/mp4" />
        </video>
      </div>

      {/* No underscore in the names: Tailwind reads `_` in an arbitrary value
          as a space, so `sm_qrCut` would never match a keyframe. */}
      <style>{`
        @keyframes smQrCut{
          0%{clip-path:inset(0 0 100% 0)}
          100%{clip-path:inset(0 0 0 0)}
        }
        @keyframes smQrHead{
          0%{top:0;opacity:0}
          8%{opacity:1}
          88%{opacity:1}
          100%{top:100%;opacity:0}
        }
        @keyframes smQrGlow{
          0%{opacity:0}
          35%{opacity:.9}
          100%{opacity:0}
        }
        @keyframes smNameTurn{
          from{opacity:1;scale:1 1}
          to{opacity:0;scale:1 .15}
        }
        @media (prefers-reduced-motion:reduce){
          [class*="smQrCut"],[class*="smQrHead"],[class*="smQrGlow"]{animation-duration:.01ms!important}
        }
      `}</style>
    </section>
  );
}
